import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useConfiguration } from '../../context/configurationContext';
import { parse } from '../../util/urlHelpers';
import { 
  getActiveBrandName, 
  getActiveConnectivityName, 
  getActiveCompatibilityName,
  isPromotionsPath,
} from './SearchPage.shared';
import {
  buildSearchParamsFromSlugEntry,
  findSlugEntry,
  getCategoryEntryById,
  getSubcategoryEntryByIds,
} from '../../util/categorySlugMap';

import css from './SearchPage.module.css';

/**
 * SearchBreadcrumb component displays category navigation breadcrumb on search page
 * 
 * @param {Object} queryParams - URL query parameters containing category, subcategory, and subsubcategory
 * @param {Function} onResetAll - Function to call when resetting all filters
 */
const SearchBreadcrumb = ({ queryParams, onResetAll }) => {
  const config = useConfiguration();
  const history = useHistory();
  const location = useLocation();
  const [isExpanded, setIsExpanded] = useState(false);
  
  if (!queryParams) {
    return null;
  }

  // Check if deals parameter is active (read directly from URL as it's not a filter param)
  const rawQueryParams = parse(location.search);
  const isPromotionsPage = isPromotionsPath(location.pathname);
  const isDealsActive =
    rawQueryParams.deals === 'true' ||
    rawQueryParams.deals === true ||
    rawQueryParams.deals === '1' ||
    rawQueryParams.deals === 1 ||
    isPromotionsPage;

  const slugFromPath = location?.pathname?.startsWith('/products/')
    ? location.pathname.replace('/products/', '').split('/')[0]
    : null;
  // Extract category levels from query params
  // URL uses pub_categoryLevel1, pub_categoryLevel2, pub_categoryLevel3
  const slugEntry = slugFromPath ? findSlugEntry(slugFromPath) : null;
  const derivedParams = slugEntry ? buildSearchParamsFromSlugEntry(slugEntry) : {};

  const categoryLevel1 =
    queryParams.pub_categoryLevel1 ||
    derivedParams.pub_categoryLevel1 ||
    derivedParams.category;
  const categoryLevel2 =
    queryParams.pub_categoryLevel2 ||
    derivedParams.pub_categoryLevel2 ||
    derivedParams.subcategory;
  const categoryLevel3 = queryParams.pub_categoryLevel3;

  // Get categories from configuration
  const categories = config?.categoryConfiguration?.categories || [];
  const listingFieldsConfig = config?.listing?.listingFields || [];

  // Get active filter names
  const brandName = getActiveBrandName(queryParams, listingFieldsConfig);
  const connectivityName = getActiveConnectivityName(queryParams, listingFieldsConfig);
  const compatibilityName = getActiveCompatibilityName(queryParams, listingFieldsConfig);

  // Count active filters
  const activeFilters = [brandName, connectivityName, compatibilityName].filter(Boolean);
  const activeFiltersCount = activeFilters.length;

  // Determine filter label for breadcrumb
  let filterLabel = null;
  if (activeFiltersCount === 1) {
    // Single filter: show its value
    filterLabel = activeFilters[0];
  } else if (activeFiltersCount > 1) {
    // Multiple filters: show "Vue filtrée"
    filterLabel = 'Vue filtrée';
  }

  const renderBreadcrumb = crumbs => {
    const showCollapse = crumbs.length > 3;
    const listClassName = showCollapse
      ? isExpanded
        ? `${css.breadcrumbList} ${css.breadcrumbListExpanded}`
        : `${css.breadcrumbList} ${css.breadcrumbListCollapsed}`
      : css.breadcrumbList;

    return (
      <nav className={css.breadcrumb} aria-label="Breadcrumb">
        <ol className={listClassName}>
          {crumbs.map((crumb, index) => {
            const isCurrent = index === crumbs.length - 1;
            const isMiddle = index > 0 && index < crumbs.length - 1;
            const prevIsMiddle = index - 1 > 0 && index - 1 < crumbs.length - 1;
            const isSeparatorMiddle = index > 0 && (isMiddle || prevIsMiddle);
            return (
              <React.Fragment key={crumb.key}>
                {index > 0 ? (
                  <li
                    className={`${css.breadcrumbSeparator}${
                      isSeparatorMiddle ? ` ${css.breadcrumbSeparatorMiddle}` : ''
                    }`}
                    aria-hidden="true"
                  >
                    /
                  </li>
                ) : null}
                <li
                  className={`${css.breadcrumbItem}${
                    isMiddle ? ` ${css.breadcrumbItemMiddle}` : ''
                  }`}
                >
                  {!isCurrent && crumb.onClick ? (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        crumb.onClick();
                      }}
                      className={css.breadcrumbLink}
                    >
                      {crumb.label}
                    </button>
                  ) : (
                    <span className={css.breadcrumbLinkCurrent} aria-current="page">
                      {crumb.label}
                    </span>
                  )}
                </li>
                {index === 0 && showCollapse ? (
                  <>
                    <li
                      className={`${css.breadcrumbSeparator} ${css.breadcrumbSeparatorCollapsed}`}
                      aria-hidden="true"
                    >
                      /
                    </li>
                    <li className={`${css.breadcrumbItem} ${css.breadcrumbItemCollapsed}`}>
                      <button
                        type="button"
                        className={css.breadcrumbEllipsisButton}
                        onClick={() => setIsExpanded(true)}
                        aria-label="Afficher le fil d'ariane complet"
                      >
                        ...
                      </button>
                    </li>
                    <li
                      className={`${css.breadcrumbSeparator} ${css.breadcrumbSeparatorCollapsed}`}
                      aria-hidden="true"
                    >
                      /
                    </li>
                  </>
                ) : null}
              </React.Fragment>
            );
          })}
        </ol>
      </nav>
    );
  };

  // If no category and no filters
  if (!categoryLevel1 && !filterLabel) {
    if (isDealsActive) {
      // Show: Tous les produits / Bons plans & Promotions
      return renderBreadcrumb([
        {
          key: 'all-products',
          label: 'Tous les produits',
          onClick: () => history.push(allProductsUrl),
        },
        {
          key: 'deals',
          label: 'Bons plans & Promotions',
        },
      ]);
    }
    // Show: Tous les produits
    return renderBreadcrumb([
      {
        key: 'all-products',
        label: 'Tous les produits',
      },
    ]);
  }

  // If no category but filters exist
  if (!categoryLevel1 && filterLabel) {
    if (isDealsActive) {
      // Show: Tous les produits / Bons plans & Promotions / Filter
      return renderBreadcrumb([
        {
          key: 'all-products',
          label: 'Tous les produits',
          onClick: () => history.push(allProductsUrl),
        },
        {
          key: 'deals',
          label: 'Bons plans & Promotions',
          onClick: () => history.push(dealsUrl),
        },
        {
          key: 'filter',
          label: filterLabel,
        },
      ]);
    }
    // Show: Tous les produits / Filter
    return renderBreadcrumb([
      {
        key: 'all-products',
        label: 'Tous les produits',
        onClick: () => history.push(allProductsUrl),
      },
      {
        key: 'filter',
        label: filterLabel,
      },
    ]);
  }

  /**
   * Get display name for a category by its ID
   * @param {string} categoryId - The category ID
   * @returns {string} - The display name or the ID if not found
   */
  const getCategoryDisplayName = (categoryId) => {
    try {
      const found = categories.find(c => c.id === categoryId);
      if (found && found.name) return found.name;
      // Fallback: title-case the technical ID
      if (typeof categoryId === 'string') {
        return categoryId
          .replace(/-/g, ' ')
          .replace(/\b\w/g, ch => ch.toUpperCase());
      }
      return categoryId;
    } catch (_) {
      return categoryId;
    }
  };

  /**
   * Get display name for a subcategory by its ID
   * @param {string} categoryId - The parent category ID
   * @param {string} subcategoryId - The subcategory ID
   * @returns {string} - The display name or the ID if not found
   */
  const getSubcategoryDisplayName = (categoryId, subcategoryId) => {
    try {
      const category = categories.find(c => c.id === categoryId);
      if (category && category.subcategories) {
        const found = category.subcategories.find(sc => sc.id === subcategoryId);
        if (found && found.name) return found.name;
      }
      // Fallback: title-case the technical ID
      if (typeof subcategoryId === 'string') {
        return subcategoryId
          .replace(/-/g, ' ')
          .replace(/\b\w/g, ch => ch.toUpperCase());
      }
      return subcategoryId;
    } catch (_) {
      return subcategoryId;
    }
  };

  /**
   * Get display name for a sub-subcategory (level 3) by its ID
   * @param {string} categoryId - The parent category ID
   * @param {string} subcategoryId - The subcategory ID (level 2)
   * @param {string} subSubcategoryId - The sub-subcategory ID (level 3)
   * @returns {string} - The display name or the ID if not found
   */
  const getSubSubcategoryDisplayName = (categoryId, subcategoryId, subSubcategoryId) => {
    try {
      const category = categories.find(c => c.id === categoryId);
      if (category && category.subcategories) {
        const subcategory = category.subcategories.find(sc => sc.id === subcategoryId);
        if (subcategory && subcategory.subcategories) {
          const found = subcategory.subcategories.find(ssc => ssc.id === subSubcategoryId);
          if (found && found.name) return found.name;
        }
      }
      // Fallback: title-case the technical ID
      if (typeof subSubcategoryId === 'string') {
        return subSubcategoryId
          .replace(/-/g, ' ')
          .replace(/\b\w/g, ch => ch.toUpperCase());
      }
      return subSubcategoryId;
    } catch (_) {
      return subSubcategoryId;
    }
  };

  // Get display labels
  const categoryLabel = getCategoryDisplayName(categoryLevel1);
  const subcategoryLabel = categoryLevel2 
    ? getSubcategoryDisplayName(categoryLevel1, categoryLevel2) 
    : null;
  const subSubcategoryLabel = categoryLevel3
    ? getSubSubcategoryDisplayName(categoryLevel1, categoryLevel2, categoryLevel3)
    : null;
  const categoryEntry = categoryLevel1 ? getCategoryEntryById(categoryLevel1) : null;
  const subcategoryEntry =
    categoryLevel1 && categoryLevel2
      ? getSubcategoryEntryByIds(categoryLevel1, categoryLevel2)
      : null;
  const categoryUrl = categoryEntry ? `/products/${categoryEntry.slug}` : `/products?category=${categoryLevel1}`;
  const subcategoryUrl = subcategoryEntry
    ? `/products/${subcategoryEntry.slug}`
    : `/products?category=${categoryLevel1}&subcategory=${categoryLevel2}`;
  const allProductsUrl = isPromotionsPage ? '/promotions' : '/products';
  const dealsUrl = isPromotionsPage ? '/promotions' : '/products?deals=true';
  const categoryUrlInDeals = isPromotionsPage
    ? `/promotions?category=${categoryLevel1}`
    : `/products?deals=true&category=${categoryLevel1}`;
  const subcategoryUrlInDeals = isPromotionsPage
    ? `/promotions?category=${categoryLevel1}&subcategory=${categoryLevel2}`
    : `/products?deals=true&category=${categoryLevel1}&subcategory=${categoryLevel2}`;
  const subSubcategoryUrlInDeals = isPromotionsPage
    ? `/promotions?category=${categoryLevel1}&subcategory=${categoryLevel2}&subsubcategory=${categoryLevel3}`
    : `/products?deals=true&category=${categoryLevel1}&subcategory=${categoryLevel2}&subsubcategory=${categoryLevel3}`;


  const crumbs = [
    {
      key: 'all-products',
      label: 'Tous les produits',
      onClick: () => history.push(allProductsUrl),
    },
  ];

  if (isDealsActive) {
    crumbs.push({
      key: 'deals',
      label: 'Bons plans & Promotions',
      onClick: () => history.push(dealsUrl),
    });
  }

  crumbs.push({
    key: 'category',
    label: categoryLabel,
    onClick: () => {
      const url = isDealsActive ? categoryUrlInDeals : categoryUrl;
      history.push(url);
    },
  });

  if (subcategoryLabel) {
    crumbs.push({
      key: 'subcategory',
      label: subcategoryLabel,
      onClick: () => {
        const url = isDealsActive ? subcategoryUrlInDeals : subcategoryUrl;
        history.push(url);
      },
    });
  }

  if (subSubcategoryLabel) {
    crumbs.push({
      key: 'subsubcategory',
      label: subSubcategoryLabel,
      onClick: () => {
        const url = isDealsActive
          ? subSubcategoryUrlInDeals
          : `/products?category=${categoryLevel1}&subcategory=${categoryLevel2}&subsubcategory=${categoryLevel3}`;
        history.push(url);
      },
    });
  }

  if (filterLabel) {
    crumbs.push({
      key: 'filter',
      label: filterLabel,
    });
  }

  return renderBreadcrumb(crumbs);
};

export default SearchBreadcrumb;

